import { useState } from "react";
import type { Customer360Snapshot } from "../../../types/crm";
import { AliasEditor } from "../AliasEditor";
import { GoLiveDateEditor } from "../GoLiveDateEditor";

function relativeTime(isoString: string | null): string {
  if (!isoString) return "—";
  const diff = Date.now() - new Date(isoString).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function absoluteTime(isoString: string | null): string {
  if (!isoString) return "";
  return new Date(isoString).toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function RelativeTimestamp({ iso }: { iso: string | null }) {
  const [hovered, setHovered] = useState(false);
  if (!iso) return <span className="text-content-tertiary text-xs">—</span>;
  return (
    <span
      className="relative cursor-default underline decoration-dashed underline-offset-2 text-content-secondary text-xs"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {relativeTime(iso)}
      {hovered && (
        <span className="absolute bottom-full left-0 mb-1.5 z-50 px-2.5 py-1.5 rounded bg-gray-900 text-white text-xs whitespace-nowrap shadow-lg pointer-events-none">
          {absoluteTime(iso)}
        </span>
      )}
    </span>
  );
}

interface Props {
  snapshot: Customer360Snapshot;
}

/**
 * Compact contacts + go-live card. Lives in the bottom "Admin actions" zone
 * on the 360 page because the AliasEditor + GoLiveDateEditor are inline-
 * editable — both are input surfaces.
 */
export function OverviewSection({ snapshot }: Props) {
  const { customer, lastInteractionAt } = snapshot;

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <div className="px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Contacts & Go-Live
        </h2>
      </div>

      <div className="flex-1 p-4 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-content-tertiary font-medium mb-1.5">
            Emails
          </div>
          <AliasEditor
            customerId={customer.id}
            aliases={customer.aliases}
          />
        </div>

        <div>
          <GoLiveDateEditor
            customerId={customer.id}
            dashboardUnlockedAt={customer.dashboardUnlockedAt}
            shouldPushPricesEnabledAt={customer.shouldPushPricesEnabledAt}
          />
        </div>

        <div className="pt-3 border-t border-border-secondary">
          <div className="text-[10px] uppercase tracking-wider text-content-tertiary font-medium mb-1">
            Last interaction
          </div>
          {lastInteractionAt ? (
            <RelativeTimestamp iso={lastInteractionAt} />
          ) : (
            <span className="text-content-tertiary text-xs">No events yet</span>
          )}
        </div>
      </div>
    </section>
  );
}
